import {Component, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {NgbActiveModal, NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';

import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {PaperReportService} from './paper-report.service';
import {FormErrorHandler} from '@app/shared/form-error-handler';
import {of, ReplaySubject} from 'rxjs';
import {DealerInfoModel} from '../dealer-info/dealer-info.model';
import {DealerInfoService} from '../dealer-info/dealer-info.service';
import {MarketType} from '@app/domain/model/market-type';
import {ReportType} from '@app/domain/model/report-type';
import {ApprovalStatus} from '@app/domain/model/approval-status';
import {Seller} from '@app/feature/seller/seller';
import {QuerySellersService} from '@app/feature/seller/query-sellers.service';
import {Species, SpeciesService} from '@app/feature/species';
import {Condition, ConditionService} from '@app/feature/condition';
import {Vessel, VesselService} from '@app/feature/vessel';
import {inRange, positiveNumber} from '@app/shared/validators/purchase.validator';
import Big from 'big.js';
import {PaperReportModel} from './paper-report.model';
import {validateVesselId} from '@app/shared/validators/vessel.validator';
import {DatePickerFormatter} from '@app/util/date-picker-formatter';
import {ReportingPeriod} from '@app/feature/dealer/report/reporting-period';
import {DealerProfile} from '@app/domain/model/dealer-profile';
import {Months} from '@app/domain/model/months';
import {validateDate} from '@app/shared/validators/date.validator';
import {AppError, AppErrorDto} from '@app/domain/model/error';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';

@Component({
  providers: [{ provide: NgbDateParserFormatter, useClass: DatePickerFormatter }],
  selector: 'app-paper-report',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './paper-report.component.html',
  styleUrls: ['./paper-report.component.scss']
})
export class PaperReportComponent implements OnInit,OnDestroy {

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  @Input()
  period: ReportingPeriod;
  @Input()
  dealer: DealerProfile;
  @Input()
  month: Months;
  @Input()
  year: number;

  // make the enums' values accessible from template
  MarketType: typeof MarketType = MarketType;
  ReportType: typeof ReportType = ReportType;
  Months: typeof Months = Months;
  marketTypes: Set<MarketType> = new Set<MarketType>();
  ApprovalStatus: typeof ApprovalStatus = ApprovalStatus;

  // report form
  reportForm: FormGroup;

  readyToSubmit = false;

  loading: boolean;
  showCancelWarning: boolean;
  showDeleteRecordWarning: number;
  totalCount: Big = Big(0);
  totalPrice: Big = Big(0);
  paperReport: PaperReportModel;

  constructor(private sellersReader: QuerySellersService,
              private speciesService: SpeciesService,
              private conditionService: ConditionService,
              private vesselService: VesselService,
              private dealerInfoService: DealerInfoService,
              private activeModal: NgbActiveModal,
              readonly formErrorHandler: FormErrorHandler,
              private readonly formBuilder: FormBuilder,
              private readonly paperReportService: PaperReportService) {
  }


  dealerAutocompleteFormatter = (dealerInfo: DealerInfoModel) => {
    return dealerInfo ? `${dealerInfo.licenseNumber} (${dealerInfo.businessName})` : null;
  }
  dealerAutocompleteInputFormatter = (dealerInfo: DealerInfoModel) => {
    return dealerInfo ? dealerInfo.licenseNumber.toString() : null;
  }
  dealerAutocomplete = (term: string) => {
    if (term.length < 3) {
      return of([]);
    }

    return this.dealerInfoService.getDealers(term);
  }


  licenseAutocompleteFormatter = (seller: Seller) => seller ? `${seller.licenseNumber} (${seller.name})` : null;


  licenseAutocomplete = (term: string) => {
    if (term.length < 3) {
      return of([]);
    }

    return this.sellersReader.querySellers(term);
  }

  speciesAutocompleteFormatter = (species: Species) => {
    if (!species) {
      return null;
    }

    return species.localNames ? `${species.id} - ${species.name} (${species.localNames})` : `${species.id} - ${species.name}`;
  }

  speciesAutocomplete = (term: string) => this.speciesService.getSpecies(term, this.reportForm.get('reportType').value);

  conditionAutocompleteFormatter = (condition: Condition) => {
    if (!condition) {
      return null;
    }
    return `${condition.description} (${condition.code})`;
  }

  conditionAutocomplete = (term: string) => this.conditionService.getCondition(term);

  vesselAutocompleteFormatter = (vessel: Vessel) => {
    if (!vessel) {
      return null;
    }

    if (typeof vessel === 'string') {
      return vessel;
    }

    return vessel.name ? `${vessel.registrationNumber} (${vessel.name})` : vessel.registrationNumber;
  }

  vesselAutocomplete = (term: string, index: number) => {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const control = quickReports.at(index).get('seller');

    return this.vesselService.getVessel(term, control.value.licenseNumber);
  }

  ngOnInit() {
    this.initForm();

    if (this.dealer) {
      // let dealer: DealerInfoModel = new DealerInfoModel(this.dealer.id, this.dealer.contactName,
      // this.dealer.marketTypes);
      this.reportForm.patchValue({
        dealer: this.dealer.id,
        dealerName: this.dealer.businessName
      });
      this.reportForm.get('dealer').markAsTouched();
      this.marketTypes = this.dealer.marketTypes;
      if (this.marketTypes.has(MarketType.NAQ)) {
        this.reportForm.get('reportType').patchValue(ReportType.P);
      } else if (this.marketTypes.has(MarketType.AQ)) {
        this.reportForm.get('reportType').patchValue(ReportType.PAQ);
      }
      this.reportForm.get('reportType').markAsTouched();
    }
    if (typeof this.month !== 'undefined'){
      this.reportForm.patchValue({ month: this.month });
    }
    if (typeof this.year !== 'undefined'){
      this.reportForm.get('year').patchValue(this.year);
    } else {
      this.reportForm.get('year').patchValue((new Date()).getFullYear());
    }

  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private initForm() {

    const formFields = {
      dealer: ['', Validators.required],
      dealerName: [''],
      reportType: ['', Validators.required],
      year: ['', [Validators.required, Validators.max(9999)]],
      month: ['', [Validators.required, Validators.max(12)]],
      dateReceived: ['', [Validators.required, validateDate]],
      quickReports: this.formBuilder.array([])

    };

    this.reportForm = this.formBuilder.group(formFields);
    this.addQuickReports(10);
  }

  addQuickReports(count: number, setFocus: boolean = false) {
    for (let i = 0; i < count; i++) {
      this.addQuickReport();
    }
    if(setFocus) {
      setTimeout(() => {
        //put focus at the beginning of new array form
        let i = 0;
        let inputEl: HTMLInputElement;
        do {
          inputEl = <HTMLInputElement>document.getElementById('elem-' + i + '-day');
          i++;
        } while (inputEl.value !== '');

        if (inputEl) {
          inputEl.focus();
        }

      }, 1);
    }
  }

  addQuickReport() {
    const quickReports = this.reportForm.controls['quickReports'] as FormArray;

    const purchase = this.formBuilder.group({
      day: [null, [Validators.required, Validators.max(31)]],
      seller: [null, Validators.required],
      species: [null, Validators.required],
      lbsBought: [null],
      piecesBought: [null],
      condition: [null],
      vesselId: [null, [validateVesselId]],
      comments: [null, [Validators.maxLength(60), Validators.pattern(/^[^"^\n]*$/)]],
      status: [null],
      value: [null]
    });

    const reportType = this.reportForm.get('reportType');
    reportType.valueChanges.pipe(takeUntil(this.destroyed$))
      .pipe(takeUntil(this.destroyed$))
      .subscribe(selectedType => this.initPurchaseValidators(selectedType, purchase));

    const species = purchase.get('species');
    species.valueChanges
      .pipe(distinctUntilChanged())
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        $ => {
          this.initPurchaseValidators(reportType.value, purchase);
        }
      );

    quickReports.push(purchase);
  }

  removeQuickReport(idx: number) {
    const quickReportFormArray = this.reportForm.get('quickReports') as FormArray;
    if (!quickReportFormArray.at(idx).dirty || this.showDeleteRecordWarning === idx) {
      quickReportFormArray.removeAt(idx);
      this.reportForm.markAsDirty();
      this.showDeleteRecordWarning = null;
    } else {
      this.showDeleteRecordWarning = idx;
    }
  }

  onCancel() {
    if (this.showCancelWarning) {
      this.activeModal.close(false);
    }
    this.showCancelWarning = true;
  }


  onReview() {
    this.showCancelWarning = false;

    if (!this.isValid()) {
      return;
    }

    this.reviewPaperReport();
  }

  onSubmit() {
    this.showCancelWarning = false;

    if (!this.isValid()) {
      return;
    }

    this.submitPaperReport();
  }

  onBack() {
    this.readyToSubmit = false;
    this.showCancelWarning = false;
  }

  private submitPaperReport() {
    this.loading = true;
    this.paperReportService.addPaperReport(this.paperReport)
      .subscribe(() => {
        this.activeModal.close(true);
        this.loading = false;
      });
  }

  private reviewPaperReport() {
    this.paperReport = this.getReport();
    this.loading = true;
    this.paperReportService.validatePaperReport(this.paperReport)
      .subscribe(
        () => {
          this.loading = false;
          this.readyToSubmit = true;
        },
        this.invalidRequestHandler
      );
  }

  private invalidRequestHandler = (error: AppError) => {
    this.loading = false;
    let errorMatched = false;
    if(error.error && typeof error.error === 'object' && Array.isArray(error.error)){
      error.error.forEach(((value: AppErrorDto) => {
        if (value.field) {
          const control: AbstractControl = this.reportForm.get(value.field);
          if (control && value.code) {
            const err: ValidationErrors = {};
            err[value.code] = true;
            control.setErrors(err);
            errorMatched = true;
          }
        }
      }));
    }
    if (!errorMatched) {
      throw error;
    }
  }

  private getReport(): PaperReportModel {
    const dealer = this.reportForm.get('dealer').value;
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const quickReportsTouched: AbstractControl[] = quickReports.controls.filter((formGroup: AbstractControl) => formGroup.dirty);

    const report = Object.assign({}, this.reportForm.value,
      {
        dealerId: dealer instanceof Object ? dealer.licenseNumber : dealer,
        dealerName: this.reportForm.get('dealerName').value,
        year: this.reportForm.controls['year'].value,
        month: this.reportForm.controls['month'].value,
        dateReceived: this.reportForm.controls['dateReceived'].value,
        reportType: this.reportForm.get('reportType').value,
        paperPurchaseReportItems: quickReportsTouched.map(
          (purchase) => {

            let purchaseReport: any; // TODO: typings for QuickReportModel.purchase
            const reportType = this.reportForm.get('reportType').value;
            const species: Species = purchase.get('species').value;

            if (ReportType.P === reportType) {
              purchaseReport = {
                condition: purchase.get('condition').value,
                lbsBought: purchase.get('lbsBought').value,
                piecesBought: purchase.get('piecesBought').value,
                value: purchase.get('value').value,
                foodSpecies: {
                  id: species.id,
                  name: species.name,
                  localNames: species.localNames
                }
              };
            } else if (ReportType.PAQ === reportType) {
              purchaseReport = {
                piecesBought: purchase.get('piecesBought').value,
                value: purchase.get('value').value,
                aqSpecies: {
                  id: species.id,
                  name: species.name,
                  localNames: species.localNames
                }
              };
            }
            const seller: Seller = purchase.get('seller').value;
            const vesselId = purchase.get('vesselId').value;
            return {
              day: purchase.get('day').value,
              sellerId: seller.licenseNumber,
              sellerName: seller.businessName ? seller.businessName : seller.name,
              purchase: purchaseReport,
              remarks: purchase.get('comments').value,
              vessel: {
                id: vesselId
              },
              status: ApprovalStatus[purchase.get('status').value]
            };
          })
      });
    return report;
  }

  onDealerSelect(dealerInfo: DealerInfoModel) {
    this.reportForm.patchValue({
      dealer: dealerInfo,
      dealerName: dealerInfo.businessName
    });
    this.marketTypes = dealerInfo.marketTypes;
    if(this.marketTypes.size === 1){
      if (this.marketTypes.has(MarketType.NAQ)) {
        this.reportForm.get('reportType').patchValue(ReportType.P);
      } else if (this.marketTypes.has(MarketType.AQ)) {
        this.reportForm.get('reportType').patchValue(ReportType.PAQ);
      }
      this.reportForm.get('reportType').markAsTouched();
    } else {
      this.reportForm.get('reportType').patchValue('');
    }
  }

  onDealerEnter(dealerInfo: DealerInfoModel) {
    if (!dealerInfo) {
      this.reportForm.patchValue({
        dealer: null,
        dealerName: ''
      });
      this.marketTypes.clear();
    }
    this.reportForm.get('dealer').markAsTouched();
  }

  onSellerSelect(seller: Seller, index: number) {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const control = quickReports.at(index).get('seller');
    control.patchValue(seller);
  }

  onSellerEnter(seller: string, index: number) {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const control = quickReports.at(index).get('seller');
    if (!seller) {
      control.patchValue(null);
    }
    control.markAsTouched();
  }

  onSpeciesSelect(species: Species, index: number) {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const control = quickReports.at(index).get('species');
    control.patchValue(species);
    this.clearAmounts(index);
  }

  onSpeciesFocusOut(species: string, index: number) {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const control = quickReports.at(index).get('species');
    if (!species) {
      control.patchValue(null);
    }
    control.markAsTouched();
  }

  onConditionSelect(condition: Condition, index: number) {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const control = quickReports.at(index).get('condition');
    control.patchValue(condition);
  }

  onConditionFocusOut(condition: string, index: number) {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const control = quickReports.at(index).get('condition');
    if (!condition) {
      control.patchValue(null);
    }
    control.markAsTouched();
  }

  onVesselSelect(vessel: Vessel, index: number) {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const report = quickReports.at(index);
    report.patchValue({
      vesselId: vessel.registrationNumber ? vessel.registrationNumber : null
    });
  }

  onVesselEnter(vessel: string, index: number) {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const vesselId = quickReports.at(index).get('vesselId');

    if (vessel !== vesselId.value) {
      vesselId.patchValue(vessel);
    }
    vesselId.markAsTouched();
  }

  onReportTypeChange(reportType: ReportType) {
    this.resetSpecies();
    this.redefinePurchaseValidators(reportType);
  }

  onPurchaseUpdate() {
    const quickReports = this.reportForm.get('quickReports') as FormArray;

    this.totalPrice = quickReports.controls
      .map(control => {
        if (!control.get('value').value ||
          isNaN(control.get('value').value)) {
          return Big(0);
        }
        return Big(control.get('value').value);
      })
      .reduce((a: Big, b: Big) => a.add(b), Big(0));

    if (ReportType.P === this.reportForm.get('reportType').value) {
      this.totalCount = quickReports.controls
        .map(control => control.get('lbsBought').value || 0)
        .filter(maybeAmount => !isNaN(maybeAmount))
        .map(amount => Big(amount))
        .reduce((a: Big, b: Big) => a.add(b), Big(0));

    } else if (ReportType.PAQ === this.reportForm.get('reportType').value) {
      this.totalCount = quickReports.controls
        .map(control => control.get('piecesBought').value || 0)
        .filter(maybeAmount => !isNaN(maybeAmount))
        .map(amount => Big(amount))
        .reduce((a: Big, b: Big) => a.add(b), Big(0));
    }
  }

  resetSpecies() {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    quickReports.controls.forEach((control) => {
      control.patchValue({
        species: null
      });
    });

  }

  redefinePurchaseValidators(reportType: ReportType) {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    quickReports.controls
      .forEach((purchase) => {
        this.initPurchaseValidators(reportType, purchase);
      });
  }

  private initPurchaseValidators(reportType: ReportType, purchase: AbstractControl) {
    const lbsBought = purchase.get('lbsBought');
    const piecesBought = purchase.get('piecesBought');
    const species = purchase.get('species').value;
    const value = purchase.get('value');

    if (this.requiresPcs(reportType, species)) {
      lbsBought.clearValidators();
      piecesBought.setValidators([positiveNumber(), inRange(0, 1000000)]);
    } else {
      lbsBought.setValidators([positiveNumber(), inRange(0, 1000000)]);
      if (species && !species.pieceExempt) {
        piecesBought.setValidators([positiveNumber(), inRange(0, 1000000)]);
      } else {
        piecesBought.clearValidators();
      }
    }
    value.setValidators([positiveNumber(), inRange(0, 1000000000)]);

    value.updateValueAndValidity();
    lbsBought.updateValueAndValidity();
    piecesBought.updateValueAndValidity();

  }

  isEmpty(): boolean {
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    return !quickReports.controls.some(
      (purchase, index, $) => {
        const species = purchase.get('species').value as Species;
        return species && (null != species.id);
      });
  }

  isValid(): boolean {
    if (this.reportForm.controls['dealer'].invalid
      || this.reportForm.controls['dealerName'].invalid
      || this.reportForm.controls['reportType'].invalid
      || this.reportForm.controls['year'].invalid
      || this.reportForm.controls['month'].invalid
      || this.reportForm.controls['dateReceived'].invalid) {
      return false;
    }
    const quickReportFormArray = this.reportForm.controls['quickReports'] as FormArray;
    const invalidAndTouchedReports: AbstractControl[] = quickReportFormArray.controls.filter((formGroup: AbstractControl) => formGroup.dirty && formGroup.invalid);
    return invalidAndTouchedReports.length === 0;
  }

  moveToNextOnEnter(event: KeyboardEvent, id: string) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const inputEl: HTMLElement = document.getElementById(id);
      if (inputEl) {
        inputEl.focus();
      }
      return false;
    }
  }

  isLbsBased(index: number): boolean {
    const reportType = this.reportForm.get('reportType').value;
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const species = quickReports.at(index).get('species').value as Species;
    return (ReportType.P === reportType) && !(species && species.lbsExempt);
  }

  isPcsBased(index: number): boolean {
    const reportType = this.reportForm.get('reportType').value;
    const quickReports = this.reportForm.get('quickReports') as FormArray;
    const species = quickReports.at(index).get('species').value as Species;
    return this.requiresPcs(reportType, species);
  }

  private requiresPcs(reportType: ReportType, species: Species) {
    return (ReportType.PAQ === reportType) || (species && species.lbsExempt);
  }
  private clearAmounts(index: number){

    const purchases = this.reportForm.get('quickReports') as FormArray;
    const quickReport = purchases.at(index);

    quickReport.get('lbsBought').patchValue('');
    quickReport.get('piecesBought').patchValue('');
    quickReport.get('value').patchValue('');

  }

}

